/**
 * Shared color-by grouping for Scatter History and Probability History explore pages.
 */
import type { ScatterHistoryDataMode } from '../services/scatterHistoryDataService';
import { buildColorGrouping, getGroupKeyFromItem } from './colorGrouping';
import { defaultChannelsStore } from '../store/defaultChannelsStore';
import { getScatterDayKey } from './scatterColors';

export const EXPLORE_HISTORY_BASE_COLOR_TYPES = [
  'DEFAULT',
  'GRADE',
  'TACK',
  'UW/DW',
  'SOURCE',
  'DAY',
] as const;

export const EXPLORE_HISTORY_AGG_COLOR_TYPES = ['EVENT', 'YEAR', 'CONFIG', 'CREW'] as const;

export type ExploreHistoryColorType =
  | (typeof EXPLORE_HISTORY_BASE_COLOR_TYPES)[number]
  | (typeof EXPLORE_HISTORY_AGG_COLOR_TYPES)[number];

export interface ExploreColorGroup {
  key: string;
  label: string;
  color: string;
  rows: Record<string, unknown>[];
}

export function colorTypesForDataMode(mode: ScatterHistoryDataMode): string[] {
  const base = [...EXPLORE_HISTORY_BASE_COLOR_TYPES];
  if (mode === 'dataset_aggregates' || mode === 'maneuver_stats' || mode === 'start_stats') {
    return [...base, ...EXPLORE_HISTORY_AGG_COLOR_TYPES];
  }
  return base;
}

export function isExploreHistoryColorTypeValid(
  colorType: string,
  mode: ScatterHistoryDataMode
): boolean {
  return colorTypesForDataMode(mode).includes(colorType);
}

function getTwaFromRow(row: Record<string, unknown>, twaChannelName: string): number {
  const raw = row[twaChannelName] ?? row.Twa ?? row.twa ?? row.TWA;
  const n = typeof raw === 'number' ? raw : Number(raw);
  return Number.isFinite(n) ? n : 0;
}

const GRADE_COLORS: Record<number, string> = {
  0: 'lightgray',
  1: 'red',
  2: 'lightgreen',
  3: 'darkgreen',
};

export function groupKeyToLegendLabel(colortype: string, key: string): string {
  const ct = colortype || 'DEFAULT';
  if (ct === 'GRADE') {
    const g = Number(key);
    return Number.isFinite(g) ? `Grade ${g}` : `Grade ${key}`;
  }
  if (ct === 'TACK') {
    if (key === 'STBD') {return 'Starboard';}
    if (key === 'PORT') {return 'Port';}
  }
  if (ct === 'UW/DW') {
    if (key === 'UPWIND') {return 'Upwind (<75°)';}
    if (key === 'REACHING') {return 'Reaching (75–120°)';}
    if (key === 'DOWNWIND') {return 'Downwind (>120°)';}
  }
  return key || 'unknown';
}

export function getExploreRowGroupKey(
  row: Record<string, unknown>,
  colortype: string,
  twaChannelName?: string
): string {
  const ct = colortype || 'DEFAULT';
  const twa = twaChannelName ?? defaultChannelsStore.twaName();

  if (ct === 'DEFAULT') {return 'DEFAULT';}
  if (ct === 'TACK') {return getTwaFromRow(row, twa) > 0 ? 'STBD' : 'PORT';}
  if (ct === 'UW/DW') {
    const absTwa = Math.abs(getTwaFromRow(row, twa));
    if (absTwa < 75) {return 'UPWIND';}
    if (absTwa <= 120) {return 'REACHING';}
    return 'DOWNWIND';
  }
  if (ct === 'GRADE') {
    const grade = row.grade ?? row.Grade ?? row.GRADE;
    const g = typeof grade === 'number' ? grade : Number(grade);
    return Number.isFinite(g) ? String(g) : 'unknown';
  }
  if (ct === 'DAY') {return getScatterDayKey(row);}
  return String(getGroupKeyFromItem(row, ct));
}

export function buildExploreCategoryColorMap(
  rows: Record<string, unknown>[],
  colortype: string,
  options?: { twaChannelName?: string; defaultSeriesColor?: string }
): Record<string, string> {
  const groups = groupRowsForExploreColorType(rows, colortype, options);
  const map: Record<string, string> = {};
  for (const group of groups) {
    map[group.key] = group.color;
  }
  return map;
}

export function groupRowsForExploreColorType(
  rows: Record<string, unknown>[],
  colortype: string,
  options?: { twaChannelName?: string; defaultSeriesColor?: string }
): ExploreColorGroup[] {
  const ct = colortype || 'DEFAULT';
  const twa = options?.twaChannelName ?? defaultChannelsStore.twaName();
  const defaultColor = options?.defaultSeriesColor ?? '#1f77b4';
  const safeRows = rows ?? [];

  if (ct === 'DEFAULT') {
    return [
      {
        key: 'DEFAULT',
        label: 'DEFAULT',
        color: defaultColor,
        rows: safeRows,
      },
    ];
  }

  if (ct === 'TACK') {
    const port: Record<string, unknown>[] = [];
    const stbd: Record<string, unknown>[] = [];
    for (const row of safeRows) {
      if (getTwaFromRow(row, twa) > 0) {stbd.push(row);}
      else {port.push(row);}
    }
    const groups: ExploreColorGroup[] = [];
    if (port.length > 0) {
      groups.push({ key: 'PORT', label: 'Port', color: 'red', rows: port });
    }
    if (stbd.length > 0) {
      groups.push({ key: 'STBD', label: 'Starboard', color: 'green', rows: stbd });
    }
    return groups;
  }

  if (ct === 'UW/DW') {
    const upwind: Record<string, unknown>[] = [];
    const reaching: Record<string, unknown>[] = [];
    const downwind: Record<string, unknown>[] = [];
    for (const row of safeRows) {
      const absTwa = Math.abs(getTwaFromRow(row, twa));
      if (absTwa < 75) {upwind.push(row);}
      else if (absTwa <= 120) {reaching.push(row);}
      else {downwind.push(row);}
    }
    const groups: ExploreColorGroup[] = [];
    if (upwind.length > 0) {
      groups.push({ key: 'UPWIND', label: 'Upwind (<75°)', color: 'blue', rows: upwind });
    }
    if (reaching.length > 0) {
      groups.push({
        key: 'REACHING',
        label: 'Reaching (75–120°)',
        color: 'orange',
        rows: reaching,
      });
    }
    if (downwind.length > 0) {
      groups.push({
        key: 'DOWNWIND',
        label: 'Downwind (>120°)',
        color: 'purple',
        rows: downwind,
      });
    }
    return groups;
  }

  if (ct === 'GRADE') {
    const buckets = new Map<string, Record<string, unknown>[]>();
    for (const row of safeRows) {
      const key = getExploreRowGroupKey(row, ct, twa);
      if (!buckets.has(key)) {buckets.set(key, []);}
      buckets.get(key)?.push(row);
    }
    return [...buckets.entries()]
      .sort(([a], [b]) => Number(a) - Number(b))
      .map(([key, groupRows]) => {
        const gradeNum = Number(key);
        const color = GRADE_COLORS[gradeNum] ?? 'lightgray';
        return {
          key,
          label: groupKeyToLegendLabel(ct, key),
          color,
          rows: groupRows,
        };
      });
  }

  if (['SOURCE', 'DAY', 'EVENT', 'YEAR', 'CONFIG', 'CREW'].includes(ct)) {
    if (safeRows.length === 0) {return [];}
    const { groups, getItemColor } = buildColorGrouping(safeRows, ct);
    return groups
      .map((group) => {
        const key = String(group.key);
        return {
          key,
          label: groupKeyToLegendLabel(ct, key),
          color: getItemColor(group.items[0]),
          rows: group.items as Record<string, unknown>[],
        };
      })
      .sort((a, b) => a.label.localeCompare(b.label, undefined, { sensitivity: 'base' }));
  }

  return [{ key: 'DEFAULT', label: 'DEFAULT', color: defaultColor, rows: safeRows }];
}

/**
 * Normalize metadata fields on explore history scatter/probability points.
 */
export function normalizeExploreHistoryPointFields(
  row: Record<string, unknown>,
  extras: {
    Datetime?: unknown;
    ID?: number;
    x?: number;
    y?: number;
    twaChannelName: string;
    twaValue?: unknown;
    gradeValue?: unknown;
    color?: unknown;
  }
): Record<string, unknown> {
  const twa = extras.twaValue;
  const grade = extras.gradeValue;
  const sourceName = row.source_name ?? row.SOURCE_NAME;
  const event = row.event ?? row.Event ?? row.EVENT;
  const year = row.year ?? row.Year ?? row.YEAR;
  const config = row.config ?? row.Config ?? row.CONFIG;

  return {
    ...row,
    Datetime: extras.Datetime,
    ...(extras.ID != null ? { ID: extras.ID } : {}),
    ...(extras.x !== undefined ? { x: extras.x } : {}),
    ...(extras.y !== undefined ? { y: extras.y } : {}),
    ...(extras.color !== undefined ? { color: extras.color } : {}),
    [extras.twaChannelName]: twa,
    Twa: twa,
    Grade: grade ?? null,
    grade: grade ?? null,
    ...(sourceName !== undefined ? { source_name: sourceName, SOURCE_NAME: sourceName } : {}),
    ...(event !== undefined ? { Event: event, event } : {}),
    ...(year !== undefined ? { Year: year, year } : {}),
    ...(config !== undefined ? { Config: config, config } : {}),
  };
}
